import React from "react";
import Header from "../../components/Header";
import "./Login.css";
import { getAuth, createUserWithEmailAndPassword, GoogleAuthProvider, GithubAuthProvider, signInWithPopup } from "firebase/auth";
import { getDatabase, ref, child, update, get } from "firebase/database";
import { useNavigate, Link } from "react-router-dom";
import GoogleLogo from "../../components/images/google.png";
import GitbhubLogo from "../../components/images/github.png";
import { SignupPageProps, SignupPageState, InputFieldProps, ThirdPartyLoginProps } from "../../types";

// Define InputField component outside to prevent recreation
const InputField: React.FC<InputFieldProps> = ({ type, value, onChange, placeholder, className, minLength, required, ...rest }) => (
    <div className={`user-box ${className || ''}`}>
        <input
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className={className}
            minLength={minLength ? parseInt(minLength) : undefined}
            required={required}
            {...rest}
        />
    </div>
);

// Define ThirdPartyLogin component outside to prevent recreation
const ThirdPartyLogin: React.FC<ThirdPartyLoginProps> = ({ onClick, title, logoSrc, logoAlt, darkMode }) => {
    const getClassName = (base: string, darkVariant: string) => darkMode ? darkVariant : base;

    return (
        <div className="GoogleLogin" onClick={onClick}>
            <button className={getClassName("loginWithGoogle", "loginWithGoogle-dark")}>Sign up with </button>
            <img title={title} className="GoogleLogo" src={logoSrc} alt={logoAlt} />
        </div>
    );
};

// Create a wrapper component to use hooks
function SignupPageWrapper(props: { darkMode: boolean }) {
    const navigate = useNavigate();
    return <SignupPage {...props} navigate={navigate} />;
}

class SignupPage extends React.Component<SignupPageProps, SignupPageState> {
    constructor(props: SignupPageProps) {
        super(props);
        this.state = {
            email: "",
            password: "",
            confirmPassword: "",
            accountType: "job_seeker",
            companyName: "",
            error: null,
            isLoading: false,
            showAccountTypeModal: false,
            pendingUser: null
        };

        this.onChange = this.onChange.bind(this);
        this.createUser = this.createUser.bind(this);
        this.loginWithGoogle = this.loginWithGoogle.bind(this);
        this.loginWithGithub = this.loginWithGithub.bind(this);
        this.validateForm = this.validateForm.bind(this);
    }

    onChange = (field: keyof SignupPageState) => (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ [field]: e.target.value } as any, () => {
            // Clear error when user starts typing
            if (this.state.error) {
                this.setState({ error: null });
            }

            // Real-time password validation
            if (field === 'confirmPassword' || field === 'password') {
                this.validatePasswords();
            }
        });
    }

    validatePasswords = (): void => {
        const { password, confirmPassword } = this.state;

        // Check password strength first
        const strengthError = this.checkPasswordStrength(password);

        // Check password match
        if (confirmPassword && password !== confirmPassword) {
            this.setState({ error: "Passwords do not match" });
        } else if (strengthError) {
            this.setState({ error: strengthError });
        } else if (this.state.error && (this.state.error === "Passwords do not match" || this.state.error.includes("Password must"))) {
            // Clear the error if passwords now match and are strong
            this.setState({ error: null });
        }
    }

    checkPasswordStrength = (password: string): string | null => {
        if (!password) return null;

        const criteria = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
        };

        const failedCriteria = Object.entries(criteria)
            .filter(([key, passed]) => !passed)
            .map(([key]) => key);

        if (failedCriteria.length > 0) {
            const requirements = [];
            if (!criteria.length) requirements.push("8+ characters");
            if (!criteria.uppercase) requirements.push("uppercase");
            if (!criteria.lowercase) requirements.push("lowercase");
            if (!criteria.number) requirements.push("number");
            if (!criteria.special) requirements.push("special char");

            return `Password must have: ${requirements.join(", ")}`;
        }

        return null;
    }

    validateForm = (): boolean => {
        const { email, password, confirmPassword, accountType, companyName } = this.state;

        if (!email || !password || !confirmPassword) {
            this.setState({ error: "Please fill in all fields" });
            return false;
        }

        if (password !== confirmPassword) {
            this.setState({ error: "Passwords do not match" });
            return false;
        }

        // Check password strength
        const strengthError = this.checkPasswordStrength(password);
        if (strengthError) {
            this.setState({ error: strengthError });
            return false;
        }

        if (accountType === "company" && !companyName.trim()) {
            this.setState({ error: "Please enter your company name" });
            return false;
        }

        this.setState({ error: null });
        return true;
    }

    createUser = (): void => {
        if (!this.validateForm()) {
            return;
        }

        this.setState({ isLoading: true });
        const auth = getAuth();
        const { email, password, accountType, companyName } = this.state;

        createUserWithEmailAndPassword(auth, email, password)
            .then((userCredential) => {
                const user = userCredential.user;
                const dbRef = ref(getDatabase());

                const userData: Record<string, unknown> = {
                    email: user.email,
                    metadata: user.metadata,
                    accountType: accountType,
                    createdAt: new Date().toISOString()
                };

                // Add company-specific data if it's a company account
                if (accountType === "company") {
                    if (companyName) (userData as any).companyName = companyName;
                    userData.credits_balance = 3;
                    userData.credits_purchased_total = 0;
                }

                update(child(dbRef, `users/${user.uid}`), userData).then(() => {
                    this.setState({ isLoading: false });
                    this.props.navigate("/dashboard");
                }).catch((error) => {
                    console.error('Error saving user data:', error);
                    this.setState({ isLoading: false, error: "Error creating account. Please try again." });
                });
            })
            .catch((error) => {
                this.setState({
                    isLoading: false,
                    error: this.getErrorMessage(error.code)
                });
            });
    }

    getErrorMessage = (errorCode: string): string => {
        switch (errorCode) {
            case 'auth/email-already-in-use':
                return 'An account with this email already exists';
            case 'auth/invalid-email':
                return 'Please enter a valid email address';
            case 'auth/weak-password':
                return 'Password is too weak. Please choose a stronger password';
            default:
                return 'Error creating account. Please try again.';
        }
    }

    handleThirdPartyLogin = (providerInstance: GoogleAuthProvider | GithubAuthProvider): void => {
        const auth = getAuth();
        signInWithPopup(auth, providerInstance).then((result) => {
            const user = result.user;
            const dbRef = ref(getDatabase());

            // Check if this is a new user or existing user
            get(child(dbRef, `users/${user.uid}`)).then((snapshot) => {
                if (snapshot.exists()) {
                    // Existing user - redirect to dashboard
                    this.props.navigate("/dashboard");
                } else {
                    // New user - show account type selection modal
                    this.showAccountTypeModal(user);
                }
            }).catch((error) => {
                console.error('Error checking user data:', error);
                this.props.navigate("/dashboard");
            });
        }).catch((error) => {
            console.error(`Error [${error.code}]: ${error.message}`);
        });
    }

    showAccountTypeModal = (user: any): void => {
        this.setState({
            showAccountTypeModal: true,
            pendingUser: user
        });
    }

    hideAccountTypeModal = (): void => {
        this.setState({
            showAccountTypeModal: false,
            pendingUser: null,
            accountType: "job_seeker",
            companyName: ""
        });
    }

    completeThirdPartySignup = (): void => {
        const { pendingUser, accountType, companyName } = this.state;
        const dbRef = ref(getDatabase());

        const userData: Record<string, unknown> = {
            email: pendingUser.email,
            metadata: pendingUser.metadata,
            accountType: accountType,
            createdAt: new Date().toISOString()
        };

        // Add company-specific data if it's a company account
        if (accountType === "company") {
            if (companyName) (userData as any).companyName = companyName;
            userData.credits_balance = 3;
            userData.credits_purchased_total = 0;
        }

        update(child(dbRef, `users/${pendingUser.uid}`), userData).then(() => {
            this.hideAccountTypeModal();
            this.props.navigate("/dashboard");
        }).catch((error) => {
            console.error('Error saving user data:', error);
            this.hideAccountTypeModal();
            this.props.navigate("/dashboard");
        });
    }

    loginWithGithub = (): void => {
        this.handleThirdPartyLogin(new GithubAuthProvider());
    }

    loginWithGoogle = (): void => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
            'login_hint': 'user@example.com'
        });
        this.handleThirdPartyLogin(provider);
    }

    render(): React.ReactElement {
        const { darkMode } = this.props;
        const { email, password, confirmPassword, accountType, companyName, error, isLoading } = this.state;

        const getClassName = (base: string, darkVariant: string) => darkMode ? darkVariant : base;

        return (
            <div className="loginTop" {...({ darkMode } as any)}>
                <Header darkMode={darkMode} />
                <div className="login">
                    <h2 className="page-title">Create Account</h2>

                    {/* Account Type Selection */}
                    <div className="account-type-selection">
                        <h3>Account Type</h3>
                        <div className="account-type-buttons">
                            <button
                                className={`account-type-btn ${accountType === 'job_seeker' ? 'active' : ''}`}
                                onClick={() => this.setState({ accountType: 'job_seeker' })}
                            >
                                Job Seeker
                            </button>
                            <button
                                className={`account-type-btn ${accountType === 'company' ? 'active' : ''}`}
                                onClick={() => this.setState({ accountType: 'company' })}
                            >
                                Company
                            </button>
                        </div>

                        {accountType === 'company' && (
                            <InputField
                                type="text"
                                value={companyName}
                                onChange={this.onChange('companyName')}
                                placeholder="Company Name"
                            />
                        )}
                    </div>
                    <InputField type="text" value={email} onChange={this.onChange('email')} placeholder="Email" />
                    <InputField
                        type="password"
                        value={password}
                        onChange={this.onChange('password')}
                        placeholder="Password"
                        minLength="8"
                        required
                        className={confirmPassword && password !== confirmPassword ? 'password-mismatch' : ''}
                    />
                    <InputField
                        type="password"
                        value={confirmPassword}
                        onChange={this.onChange('confirmPassword')}
                        placeholder="Confirm Password"
                        minLength="8"
                        required
                        className={confirmPassword && password !== confirmPassword ? 'password-mismatch' : ''}
                    />

                    {/* Password Criteria */}
                    <div className="password-criteria">
                        Password must have: 8+ characters, uppercase, lowercase, number, special char
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    <div className="Login">
                        <button
                            className={getClassName("loginWithGoogle", "loginWithGoogle-dark")}
                            onClick={this.createUser}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Creating Account...' : 'Create Account'}
                        </button>
                    </div>

                    <ThirdPartyLogin onClick={this.loginWithGoogle} title="Google" logoSrc={GoogleLogo} logoAlt="google" darkMode={darkMode} />
                    <ThirdPartyLogin onClick={this.loginWithGithub} title="Github" logoSrc={GitbhubLogo} logoAlt="github" darkMode={darkMode} />
                    <div className="signup-link">
                        <p>Already have an account? <Link to="/login" className="signup-link-text">Log in</Link></p>
                    </div>
                </div>

                {/* Account Type Selection Modal for Third-Party Users */}
                {this.state.showAccountTypeModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h3>Complete Your Profile</h3>
                            <p>Please select your account type to continue:</p>

                            <div className="account-type-buttons">
                                <button
                                    className={`account-type-btn ${this.state.accountType === 'job_seeker' ? 'active' : ''}`}
                                    onClick={() => this.setState({ accountType: 'job_seeker' })}
                                >
                                    Job Seeker
                                </button>
                                <button
                                    className={`account-type-btn ${this.state.accountType === 'company' ? 'active' : ''}`}
                                    onClick={() => this.setState({ accountType: 'company' })}
                                >
                                    Company
                                </button>
                            </div>

                            {this.state.accountType === 'company' && (
                                <div className="user-box">
                                    <input
                                        type="text"
                                        value={this.state.companyName}
                                        onChange={this.onChange('companyName')}
                                        placeholder="Company Name"
                                    />
                                </div>
                            )}

                            <div className="modal-actions">
                                <button className="modal-btn cancel" onClick={this.hideAccountTypeModal}>
                                    Cancel
                                </button>
                                <button className="modal-btn confirm" onClick={this.completeThirdPartySignup}>
                                    Continue
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default SignupPageWrapper;
